import React from 'react';
import { Table, TableScroll, Badge, SortableTh, EmptyValue, ClampText, IdLink } from '@/components/ui';
import { humanizeCode } from '../../../utils/statusUtils';
import { Vehicle } from '../../../types/Vehicle';
import VehicleProfilePreview from './VehicleProfilePreview';

interface VehiclesTableProps {
  vehicles: Vehicle[];
  onSelectVehicle?: (vehicle: Vehicle) => void;
  sortBy?: string | null;
  loading?: boolean;
  sortDir?: 'asc' | 'desc';
  onSort?: (key: string, dir: 'asc' | 'desc') => void;
}

const VehiclesTable: React.FC<VehiclesTableProps> = ({ vehicles, onSelectVehicle, sortBy = null, sortDir = 'asc', onSort, loading = false }) => {
  const handleRowClick = (vehicle: Vehicle) => {
    if (onSelectVehicle) {
      onSelectVehicle(vehicle);
    }
  };

  // TableScroll is the page's one scroll box: sticky header, Unit Number pinned left and Actions
  // pinned right while the other columns scroll sideways (W-H5, W-M8).
  return (
    <TableScroll>
      <Table hover stickyHeader>
        <thead>
          <tr>
            <SortableTh label="Unit Number" sortKey="unitNumber" activeKey={sortBy} direction={sortDir} onSort={onSort} className="wrap-pin-left wrap-pin-edge" />
            <SortableTh label="Description" sortKey="description" activeKey={sortBy} direction={sortDir} onSort={onSort} style={{ minWidth: '18rem' }} />
            <SortableTh label="Vehicle Type" sortKey="vehicleType" activeKey={sortBy} direction={sortDir} onSort={onSort} />
            <SortableTh label="Vehicle Classification" sortKey="vehicleClassification" activeKey={sortBy} direction={sortDir} onSort={onSort} />
            <SortableTh label="Current Status" sortKey="currentStatus" activeKey={sortBy} direction={sortDir} onSort={onSort} />
            <th>Service Areas</th>
            <th>Current Yard</th>
            <th>Profile</th>
          </tr>
        </thead>
        <tbody>
          {vehicles.length === 0 ? (
            <tr>
              <td colSpan={8} className="tw-text-center tw-py-4">
                {loading ? 'Loading vehicles...' : 'No vehicles found'}
              </td>
            </tr>
          ) : (
            vehicles.map((vehicle) => (
              <tr key={vehicle.id} onClick={() => handleRowClick(vehicle)} style={{ cursor: 'pointer' }}>
                {/* V2-072 / ACT-01: the unit number is the link that opens the vehicle (no View button). */}
                <td className="wrap-pin-left wrap-pin-edge">
                  <IdLink
                    onClick={(e) => {
                      e.stopPropagation();
                      handleRowClick(vehicle);
                    }}
                  >
                    {vehicle.unitNumber}
                  </IdLink>
                </td>
                <td>{vehicle.description ? <ClampText>{vehicle.description}</ClampText> : <EmptyValue />}</td>
                <td>{vehicle.vehicleType || <EmptyValue />}</td>
                <td>{vehicle.vehicleClassification || <EmptyValue />}</td>
                <td>
                  {/* Friendly label, never the stored code: "ACTIVE" reads "Active" (W-M5). */}
                  <Badge pill bg={vehicle.currentStatus === 'In Service' ? 'success' : 'secondary'}>
                    {humanizeCode(vehicle.currentStatus || vehicle.status || '')}
                  </Badge>
                </td>
                <td>
                  {vehicle.vehicleServiceAreas && vehicle.vehicleServiceAreas.length > 0
                    ? vehicle.vehicleServiceAreas.map(sa => sa.serviceArea.name).join(', ')
                    : <EmptyValue />}
                </td>
                <td>{vehicle.currentYard?.name || <EmptyValue />}</td>
                <td>
                  {vehicle.vehicleProfile ? (
                    <div className="tw-flex tw-items-center">
                      <span>{vehicle.vehicleProfile.inspection}</span>
                      <VehicleProfilePreview profile={vehicle.vehicleProfile} />
                    </div>
                  ) : (
                    <EmptyValue />
                  )}
                </td>
              </tr>
            ))
          )}
        </tbody>
      </Table>
    </TableScroll>
  );
};

export default VehiclesTable;

